import { DeclarationBase } from "../../../../backend/src/common/declaration/DeclarationBase";
import {
    convertCerfaNumber,
    VatItemAssist,
    VatItemAuto,
} from "../../../../backend/src/common/declaration/declarationServices";
import { SheetManager } from "../../../../backend/src/common/taxform/SheetManager";
import { apolloClient } from "@/apollo.services";
import { cloneDeep } from "@apollo/client/utilities";
import FORM_TAX_FORM from "@/graphql/taxForm/form.gql";
import UPSERT_DECLARATION from "@/graphql/declaration/UpsertDeclaration2.gql";
import DECLARATION from "@/graphql/declaration/Declaration.gql";
import DELETE_SHEET from "@/graphql/declaration/DeleteSheet.gql";
import DECLARATION_TAX_FORM from "@/graphql/declarationForm/DeclarationTaxForm.gql";
import moment from "moment-timezone";
import {
    DasTwoDeclaring,
    DasTwoBeneficiary,
    Declaring,
} from "@/services/declaration/declarationServices";
import { PreviousVatCredit } from "../../../../backend/src/common/types/taxformTypes";

export class DeclarationFront extends DeclarationBase {
    private apollo = null;

    autoDeposit: AutoDeposit = null;

    declaring: Declaring = null;

    constructor(
        companyId: string,
        accountingPeriodId: string,
        declarationId: string,
        cerfaNumber: string,
        year: string,
        startDate: string,
        endDate: string,
    ) {
        super(companyId, accountingPeriodId, declarationId, cerfaNumber, year, startDate, endDate);
        this.apollo = apolloClient;
    }

    static async instanciate(
        companyId: string,
        accountingPeriodId: string,
        declarationId: string,
        cerfaNumber: string,
        year: string,
        startDate: string,
        endDate: string,
    ): Promise<DeclarationFront> {
        const declaration = new DeclarationFront(
            companyId,
            accountingPeriodId,
            declarationId,
            cerfaNumber,
            year,
            startDate,
            endDate,
        );
        await declaration.afterInstanciate();
        return declaration;
    }

    get sheetNames(): string[] {
        const names: string[] = [];
        if (this.sheets) {
            for (const sheet of this.sheets) {
                const cerfaNumber: string = sheet.cerfaNumber;
                // SPECIFIC
                // 2561
                if (cerfaNumber.includes("2561") || !names.includes(cerfaNumber)) {
                    names.push(cerfaNumber);
                } else {
                    // deduplication
                    let additionalPageNumber: number = 2;
                    while (names.includes(`${cerfaNumber} (${additionalPageNumber})`)) {
                        additionalPageNumber++;
                    }
                    names.push(`${cerfaNumber} (${additionalPageNumber})`);
                }
            }
        }
        return names;
    }

    get cerfaName(): string {
        // SPECIFIC
        // DAS2
        if (this.cerfaNumber === "DAS2") {
            return "Déclaration des honoraires";
        }
        const name = this.formsList?.find((form) => form.cerfa === this.cerfaNumber)?.title || "";
        // SPECIFIC
        // 3310CA3 + 3517SCA12
        if (name === "Déclaration de TVA et taxes assimilées - régime réel normal ou mini réel") {
            return "Déclaration de TVA 3310CA3";
        }
        if (
            name === "Déclaration de TVA et taxes assimilées - régime simplifié" &&
            this.cerfaNumber === "3517SCA12"
        ) {
            return "Déclaration de TVA 3517SCA12";
        }
        return name;
    }

    async getDeclarationQuery() {
        try {
            if (this.declarationId) {
                const declaration = await this.apollo.query({
                    query: DECLARATION,
                    variables: {
                        id: this.declarationId,
                    },
                    fetchPolicy: "network-only",
                });
                this.declarationDB = cloneDeep(declaration.data.declaration);
                this.cerfaNumber = convertCerfaNumber(
                    this.declarationDB?.sheets[0]?.form?.cerfaNumber || "",
                );
                this.declaring = this.declarationDB?.data?.declaring;
                const fileMillesim: string = this.declarationDB?.sheets[0]?.form?.year || "2024";
                this.year = fileMillesim;
            } else {
                this.declarationDB = null;
            }
        } catch (error) {
            console.error("Error getting declaration", error);
            this.declarationDB = null;
        }
    }

    async getDepositDetectionQuery() {
        try {
            if (["2571", "3514"].includes(this.cerfaNumber)) {
                const result = await this.apollo.query({
                    query: DECLARATION_TAX_FORM,
                    variables: {
                        cerfaNumber: this.cerfaNumber,
                        companyId: this.companyId,
                        startDate: this.startDate,
                        endDate: this.endDate,
                        accountingPeriodId: this.accountingPeriodId,
                        skipGetForm: true,
                    },
                });
                this.autoDeposit = result?.data?.declarationTaxForm?.result?.autoDeposit;
            }
        } catch (e) {
            debugger;
            console.error("Error in getDepositDetection", e);
        }
    }

    async getDeclarationFormQuery(year: string, cerfa: string) {
        const cerfaNumber: string = cerfa.replace(/_/g, "");
        const result = await this.apollo.query({
            query: FORM_TAX_FORM,
            variables: {
                year,
                cerfaNumber: cerfaNumber,
            },
        });
        return result.data.formTaxForm;
    }

    async saveOrDuplicateDeclarationQuery(
        decId: string,
        assistVatItems: VatItemAssist[],
        autoVatItems: VatItemAuto[],
        assistVatMode: boolean,
        autoVatMode: boolean,
        previousVatCredit: PreviousVatCredit,
        sheetsFromBundle: SheetManager[],
    ) {
        const sheetMutations = [];
        for (const sheet of this.sheets) {
            const sheetMutation = this.getSheetApolloMutation(decId, sheet);
            if (sheetMutation) {
                sheetMutations.push(sheetMutation);
            }
        }
        try {
            const startDate: Date | string =
                this.declarationDB?.startDate || moment(this.startDate).utc(true).format();
            const endDate: Date | string =
                this.declarationDB?.endDate || moment(this.endDate).utc(true).format();
            let data: {
                declaring?: Declaring;
                assistVatItems?: VatItemAssist[];
                autoVatItems?: VatItemAuto[];
                assistVatMode?: boolean;
                autoVatMode?: boolean;
                previousVatCredit?: PreviousVatCredit;
            } = {};

            // SPECIFIC
            // DAS2 + 2561
            if (["DAS2", "2561"].includes(this.cerfaNumber)) {
                data = {
                    declaring: this.declaring,
                };
            }
            //

            // SPECIFIC
            // 3310CA3 + 3517SCA12 + 3517BCA12
            if (["3310CA3", "3517SCA12", "3517BCA12"].includes(this.cerfaNumber)) {
                if (assistVatItems?.length) {
                    data.assistVatItems = assistVatItems;
                }
                if (autoVatItems?.length) {
                    data.autoVatItems = autoVatItems;
                }
                if (assistVatMode) {
                    data.assistVatMode = true;
                }
                if (autoVatMode) {
                    data.autoVatMode = true;
                }
                if (previousVatCredit) {
                    data.previousVatCredit = previousVatCredit;
                }
            }
            //

            const declaration = await this.apollo.mutate({
                mutation: UPSERT_DECLARATION,
                variables: {
                    id: decId,
                    data,
                    createSheets: !decId ? sheetMutations : [],
                    upsertSheets: decId ? sheetMutations : [],
                    companyId: this.companyId,
                    status: "EDITION",
                    accountingPeriodId: this.accountingPeriodId,
                    startDate,
                    endDate,
                },
            });
            return declaration.data.upsertDeclaration;
        } catch (e) {
            debugger;
            console.error("Error upserting declaration", e);
        }
    }

    async deleteSheetQuery(sheetId: string) {
        await this.apollo.mutate({
            mutation: DELETE_SHEET,
            variables: {
                id: sheetId,
            },
        });
    }

    getSheetApolloMutation(decId: string, sheet: SheetManager) {
        if (!sheet) {
            return null;
        }
        const values = sheet.getValues();
        if (decId) {
            const upsert = {
                where: { id: sheet.id || "" },
                create: {
                    company: { connect: { id: this.companyId } },
                    form: { connect: { id: sheet.formId } },
                    data: values,
                },
                update: {
                    data: values,
                },
            };
            return upsert;
        }
        const create = {
            company: { connect: { id: this.companyId } },
            form: { connect: { id: sheet.formId } },
            data: values,
        };
        return create;
    }

    // SPECIFIC
    // 3310CA3 + 3517SCA12 + 3517BCA12
    fillSheetsFromVatAssistAndAuto(
        previousVatCredit: PreviousVatCredit,
        assistVatItems: VatItemAssist[],
        autoVatItems: VatItemAuto[],
    ) {
        if (this.cerfaNumber === "3310CA3") {
            // 3310CA3
            if (previousVatCredit) {
                (this.sheets[1].fields["HD"].displayValue as number) = previousVatCredit.vatAmount;
            }
            if (assistVatItems?.length) {
                // assist items
                for (const item of assistVatItems) {
                    if (item.toAdd) {
                        if (item.type === "Collectée") {
                            if (item.vatRate === 20) {
                                (this.sheets[0].fields["CA"].displayValue as number) +=
                                    item.netAmount;
                                (this.sheets[0].fields["FP"].displayValue as number) +=
                                    item.netAmount;
                            } else if (item.vatRate === 5.5) {
                                (this.sheets[0].fields["CA"].displayValue as number) +=
                                    item.netAmount;
                                (this.sheets[0].fields["FB"].displayValue as number) +=
                                    item.netAmount;
                            } else if (item.vatRate === 10) {
                                (this.sheets[0].fields["CA"].displayValue as number) +=
                                    item.netAmount;
                                (this.sheets[0].fields["FR"].displayValue as number) +=
                                    item.netAmount;
                            } else if (item.vatRate === 8.5) {
                                (this.sheets[0].fields["CA"].displayValue as number) +=
                                    item.netAmount;
                                (this.sheets[0].fields["FM"].displayValue as number) +=
                                    item.netAmount;
                            } else if (item.vatRate === 2.1) {
                                (this.sheets[0].fields["CA"].displayValue as number) +=
                                    item.netAmount;
                                (this.sheets[0].fields["FN"].displayValue as number) +=
                                    item.netAmount;
                            } else if (item.vatRate === 0) {
                                (this.sheets[0].fields["DB"].displayValue as number) +=
                                    item.netAmount;
                            } else {
                                debugger;
                            }
                        } else if (item.type === "Déductible") {
                            (this.sheets[1].fields["HB"].displayValue as number) += item.vatAmount;
                        } else if (item.type === "Import") {
                            (this.sheets[0].fields["DK"].displayValue as number) += item.netAmount;
                            (this.sheets[0].fields["LB"].displayValue as number) += item.netAmount;
                            (this.sheets[0].fields["LQ"].displayValue as number) += item.vatAmount;
                            (this.sheets[1].fields["HC"].displayValue as number) += item.vatAmount;
                            (this.sheets[1].fields["LP"].displayValue as number) += item.vatAmount;
                            (this.sheets[1].fields["HF"].displayValue as number) += item.vatAmount;
                        } else if (item.type === "Intracom") {
                            (this.sheets[0].fields["CC"].displayValue as number) += item.netAmount;
                            (this.sheets[0].fields["FP"].displayValue as number) += item.netAmount;
                            (this.sheets[0].fields["GJ"].displayValue as number) += item.vatAmount;
                            (this.sheets[1].fields["HB"].displayValue as number) += item.vatAmount;
                        } else if (item.type === "Export") {
                            (this.sheets[0].fields["DA"].displayValue as number) += item.netAmount;
                        } else {
                            debugger;
                        }
                    }
                }
            }
            if (autoVatItems?.length) {
                // auto items
                for (const item of autoVatItems) {
                    if (item.toAdd) {
                        if (item.vatScheme === "Collectée") {
                            for (const detail of item.details) {
                                for (const vat of detail.vats) {
                                    if (vat.rate === 20) {
                                        (this.sheets[0].fields["CA"].displayValue as number) -=
                                            vat.netAmountSigned;
                                        (this.sheets[0].fields["FP"].displayValue as number) -=
                                            vat.netAmountSigned;
                                    } else if (vat.rate === 5.5) {
                                        (this.sheets[0].fields["CA"].displayValue as number) -=
                                            vat.netAmountSigned;
                                        (this.sheets[0].fields["FB"].displayValue as number) -=
                                            vat.netAmountSigned;
                                    } else if (vat.rate === 10) {
                                        (this.sheets[0].fields["CA"].displayValue as number) -=
                                            vat.netAmountSigned;
                                        (this.sheets[0].fields["FR"].displayValue as number) -=
                                            vat.netAmountSigned;
                                    } else if (vat.rate === 8.5) {
                                        (this.sheets[0].fields["CA"].displayValue as number) -=
                                            vat.netAmountSigned;
                                        (this.sheets[0].fields["FM"].displayValue as number) -=
                                            vat.netAmountSigned;
                                    } else if (vat.rate === 2.1) {
                                        (this.sheets[0].fields["CA"].displayValue as number) -=
                                            vat.netAmountSigned;
                                        (this.sheets[0].fields["FN"].displayValue as number) -=
                                            vat.netAmountSigned;
                                    } else if (vat.rate === 0) {
                                        if (vat.vatDescription === "Export") {
                                            (this.sheets[0].fields["DA"].displayValue as number) -=
                                                vat.netAmountSigned;
                                        } else if (!vat.vatDescription) {
                                            (this.sheets[0].fields["DB"].displayValue as number) -=
                                                vat.netAmountSigned;
                                        }
                                    } else {
                                        debugger;
                                    }
                                }
                            }
                        } else if (item.vatScheme === "Déductible") {
                            (this.sheets[1].fields["HB"].displayValue as number) +=
                                item.totalVatToDeclare;
                        } else if (item.vatScheme === "Immobilisation") {
                            (this.sheets[1].fields["HA"].displayValue as number) +=
                                item.totalVatToDeclare;
                        } else if (item.vatScheme === "Import") {
                            (this.sheets[0].fields["DK"].displayValue as number) -=
                                item.totalGrossAmountSigned;
                            (this.sheets[0].fields["LB"].displayValue as number) -=
                                item.totalGrossAmountSigned;
                            (this.sheets[0].fields["LQ"].displayValue as number) -=
                                item.totalVatToDeclare;
                            (this.sheets[1].fields["HC"].displayValue as number) -=
                                item.totalVatToDeclare;
                            (this.sheets[1].fields["LP"].displayValue as number) -=
                                item.totalVatToDeclare;
                            (this.sheets[1].fields["HF"].displayValue as number) -=
                                item.totalVatToDeclare;
                        } else if (item.vatScheme === "Acquisition intracom") {
                            (this.sheets[0].fields["CC"].displayValue as number) -=
                                item.totalGrossAmountSigned;
                            (this.sheets[0].fields["FP"].displayValue as number) -=
                                item.totalGrossAmountSigned;
                            (this.sheets[0].fields["GJ"].displayValue as number) +=
                                item.totalVatToDeclare;
                            (this.sheets[1].fields["HB"].displayValue as number) +=
                                item.totalVatToDeclare;
                        } else if (item.vatScheme === "Livraison intracom") {
                            (this.sheets[0].fields["DC"].displayValue as number) +=
                                item.totalGrossAmountSigned;
                        } else if (item.vatScheme === "Z de caisse") {
                            for (const detail of item.details) {
                                for (const vat of detail.vats) {
                                    if (vat.rate === 20) {
                                        (this.sheets[0].fields["CA"].displayValue as number) -=
                                            vat.netAmountSigned;
                                        (this.sheets[0].fields["FP"].displayValue as number) -=
                                            vat.netAmountSigned;
                                    } else if (vat.rate === 5.5) {
                                        (this.sheets[0].fields["CA"].displayValue as number) -=
                                            vat.netAmountSigned;
                                        (this.sheets[0].fields["FB"].displayValue as number) -=
                                            vat.netAmountSigned;
                                    } else if (vat.rate === 10) {
                                        (this.sheets[0].fields["CA"].displayValue as number) -=
                                            vat.netAmountSigned;
                                        (this.sheets[0].fields["FR"].displayValue as number) -=
                                            vat.netAmountSigned;
                                    } else if (vat.rate === 8.5) {
                                        (this.sheets[0].fields["CA"].displayValue as number) -=
                                            vat.netAmountSigned;
                                        (this.sheets[0].fields["FM"].displayValue as number) -=
                                            vat.netAmountSigned;
                                    } else if (vat.rate === 2.1) {
                                        (this.sheets[0].fields["CA"].displayValue as number) -=
                                            vat.netAmountSigned;
                                        (this.sheets[0].fields["FN"].displayValue as number) -=
                                            vat.netAmountSigned;
                                    } else if (vat.rate === 0) {
                                        if (
                                            vat.vatDescription.toLowerCase().includes("intracom") &&
                                            vat.netAmountSigned < 0
                                        ) {
                                            (this.sheets[0].fields["DC"].displayValue as number) -=
                                                vat.netAmountSigned;
                                        } else if (vat.netAmountSigned < 0) {
                                            (this.sheets[0].fields["DB"].displayValue as number) -=
                                                vat.netAmountSigned;
                                        }
                                    } else {
                                        debugger;
                                    }
                                }
                            }
                        } else if (item.vatScheme === "Autoliquidation") {
                            (this.sheets[0].fields["CB"].displayValue as number) -=
                                item.totalGrossAmountSigned;
                            (this.sheets[0].fields["FP"].displayValue as number) -=
                                item.totalGrossAmountSigned;
                            (this.sheets[1].fields["HB"].displayValue as number) -=
                                item.totalVatToDeclare;
                        } else if (item.vatScheme === "Export") {
                            (this.sheets[0].fields["DA"].displayValue as number) +=
                                item.totalGrossAmountSigned;
                        } else if (item.vatScheme === "Non imposable") {
                            (this.sheets[0].fields["DB"].displayValue as number) +=
                                item.totalGrossAmountSigned;
                        } else {
                            debugger;
                        }
                    }
                }
            }
        } else if (this.cerfaNumber === "3517SCA12") {
            // 3517SCA12
            if (previousVatCredit) {
                (this.sheets[0].fields["KA"].displayValue as number) = previousVatCredit.vatAmount;
            }
            if (assistVatItems?.length) {
                // assist items
                for (const item of assistVatItems) {
                    if (item.toAdd) {
                        if (item.type === "Collectée") {
                            if (item.vatRate === 20) {
                                const val: number =
                                    (this.sheets[0].fields["EW"].value as number) + item.netAmount;
                                this.sheets[0].fields["EW"].forceUserValue(val);
                            } else if (item.vatRate === 5.5) {
                                const val: number =
                                    (this.sheets[0].fields["EF"].value as number) + item.netAmount;
                                this.sheets[0].fields["EF"].forceUserValue(val);
                            } else if (item.vatRate === 10) {
                                const val: number =
                                    (this.sheets[0].fields["GF"].value as number) + item.netAmount;
                                this.sheets[0].fields["GF"].forceUserValue(val);
                            } else if (item.vatRate === 8.5) {
                                const val: number =
                                    (this.sheets[0].fields["EU"].value as number) + item.netAmount;
                                this.sheets[0].fields["EU"].forceUserValue(val);
                            } else if (item.vatRate === 2.1) {
                                const val: number =
                                    (this.sheets[0].fields["EV"].value as number) + item.netAmount;
                                this.sheets[0].fields["EV"].forceUserValue(val);
                            } else if (item.vatRate === 0) {
                                const val: number =
                                    (this.sheets[0].fields["EC"].value as number) + item.netAmount;
                                this.sheets[0].fields["EC"].forceUserValue(val);
                            } else {
                                debugger;
                            }
                        } else if (item.type === "Déductible") {
                            const val: number =
                                (this.sheets[0].fields["HA"].value as number) + item.vatAmount;
                            this.sheets[0].fields["HA"].forceUserValue(val);
                        } else if (item.type === "Intracom") {
                            (this.sheets[0].fields["EW"].displayValue as number) += item.netAmount;
                            (this.sheets[0].fields["HA"].displayValue as number) += item.vatAmount;
                        } else if (item.type === "Export") {
                            const val: number =
                                (this.sheets[0].fields["EB"].value as number) + item.netAmount;
                            this.sheets[0].fields["EB"].forceUserValue(val);
                        } else {
                            debugger;
                        }
                    }
                }
            }
            if (autoVatItems?.length) {
                // auto items
                for (const item of autoVatItems) {
                    if (item.toAdd) {
                        if (item.vatScheme === "Collectée") {
                            for (const detail of item.details) {
                                for (const vat of detail.vats) {
                                    if (vat.rate === 20) {
                                        const val: number =
                                            (this.sheets[0].fields["EW"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["EW"].forceUserValue(val);
                                    } else if (vat.rate === 5.5) {
                                        const val: number =
                                            (this.sheets[0].fields["EF"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["EF"].forceUserValue(val);
                                    } else if (vat.rate === 10) {
                                        const val: number =
                                            (this.sheets[0].fields["GF"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["GF"].forceUserValue(val);
                                    } else if (vat.rate === 8.5) {
                                        const val: number =
                                            (this.sheets[0].fields["EU"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["EU"].forceUserValue(val);
                                    } else if (vat.rate === 2.1) {
                                        const val: number =
                                            (this.sheets[0].fields["EV"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["EV"].forceUserValue(val);
                                    } else if (vat.rate === 0) {
                                        if (vat.vatDescription === "Export") {
                                            const val: number =
                                                (this.sheets[0].fields["EB"].value as number) -
                                                vat.netAmountSigned;
                                            this.sheets[0].fields["EB"].forceUserValue(val);
                                        } else if (!vat.vatDescription) {
                                            const val: number =
                                                (this.sheets[0].fields["EC"].value as number) -
                                                vat.netAmountSigned;
                                            this.sheets[0].fields["EC"].forceUserValue(val);
                                        }
                                    } else {
                                        debugger;
                                    }
                                }
                            }
                        } else if (item.vatScheme === "Déductible") {
                            const val: number =
                                (this.sheets[0].fields["HA"].value as number) +
                                item.totalVatToDeclare;
                            this.sheets[0].fields["HA"].forceUserValue(val);
                        } else if (item.vatScheme === "Immobilisation") {
                            const val: number =
                                (this.sheets[0].fields["JA"].value as number) +
                                item.totalVatToDeclare;
                            this.sheets[0].fields["JA"].forceUserValue(val);
                        } else if (item.vatScheme === "Acquisition intracom") {
                            (this.sheets[0].fields["EW"].displayValue as number) -=
                                item.totalGrossAmountSigned;
                            (this.sheets[0].fields["HA"].displayValue as number) +=
                                item.totalVatToDeclare;
                        } else if (item.vatScheme === "Livraison intracom") {
                            const val: number =
                                (this.sheets[0].fields["ED"].value as number) +
                                item.totalGrossAmountSigned;
                            this.sheets[0].fields["ED"].forceUserValue(val);
                        } else if (item.vatScheme === "Z de caisse") {
                            for (const detail of item.details) {
                                for (const vat of detail.vats) {
                                    if (vat.rate === 20) {
                                        const val: number =
                                            (this.sheets[0].fields["EW"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["EW"].forceUserValue(val);
                                    } else if (vat.rate === 5.5) {
                                        const val: number =
                                            (this.sheets[0].fields["EF"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["EF"].forceUserValue(val);
                                    } else if (vat.rate === 10) {
                                        const val: number =
                                            (this.sheets[0].fields["GF"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["GF"].forceUserValue(val);
                                    } else if (vat.rate === 8.5) {
                                        const val: number =
                                            (this.sheets[0].fields["EU"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["EU"].forceUserValue(val);
                                    } else if (vat.rate === 2.1) {
                                        const val: number =
                                            (this.sheets[0].fields["EV"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["EV"].forceUserValue(val);
                                    } else if (vat.rate === 0) {
                                        if (
                                            vat.vatDescription.toLowerCase().includes("intracom") &&
                                            vat.netAmountSigned < 0
                                        ) {
                                            const val: number =
                                                (this.sheets[0].fields["ED"].value as number) -
                                                vat.netAmountSigned;
                                            this.sheets[0].fields["ED"].forceUserValue(val);
                                        } else if (vat.netAmountSigned < 0) {
                                            const val: number =
                                                (this.sheets[0].fields["EC"].value as number) -
                                                vat.netAmountSigned;
                                            this.sheets[0].fields["EC"].forceUserValue(val);
                                        }
                                    } else {
                                        debugger;
                                    }
                                }
                            }
                        } else if (item.vatScheme === "Autoliquidation") {
                            const val: number =
                                (this.sheets[0].fields["EN"].value as number) -
                                item.totalGrossAmountSigned;
                            this.sheets[0].fields["EN"].forceUserValue(val);
                            const val2: number =
                                (this.sheets[0].fields["HA"].value as number) -
                                item.totalVatToDeclare;
                            this.sheets[0].fields["HA"].forceUserValue(val2);
                        } else if (item.vatScheme === "Export") {
                            const val: number =
                                (this.sheets[0].fields["EB"].value as number) +
                                item.totalGrossAmountSigned;
                            this.sheets[0].fields["EB"].forceUserValue(val);
                        } else if (item.vatScheme === "Non imposable") {
                            const val: number =
                                (this.sheets[0].fields["EC"].value as number) +
                                item.totalGrossAmountSigned;
                            this.sheets[0].fields["EC"].forceUserValue(val);
                        } else {
                            debugger;
                        }
                    }
                }
            }
        } else if (this.cerfaNumber === "3517BCA12") {
            // 3517BCA12
            if (previousVatCredit) {
                (this.sheets[0].fields["FC"].displayValue as number) = previousVatCredit.vatAmount;
            }
            if (assistVatItems?.length) {
                // assist items
                for (const item of assistVatItems) {
                    if (item.toAdd) {
                        if (item.type === "Collectée") {
                            if (item.vatRate === 20) {
                                const val: number =
                                    (this.sheets[0].fields["DK"].value as number) + item.netAmount;
                                this.sheets[0].fields["DK"].forceUserValue(val);
                            } else if (item.vatRate === 5.5) {
                                const val: number =
                                    (this.sheets[0].fields["DE"].value as number) + item.netAmount;
                                this.sheets[0].fields["DE"].forceUserValue(val);
                            } else if (item.vatRate === 10) {
                                const val: number =
                                    (this.sheets[0].fields["DQ"].value as number) + item.netAmount;
                                this.sheets[0].fields["DQ"].forceUserValue(val);
                            } else if (item.vatRate === 8.5) {
                                const val: number =
                                    (this.sheets[0].fields["DV"].value as number) + item.netAmount;
                                this.sheets[0].fields["DV"].forceUserValue(val);
                            } else if (item.vatRate === 2.1) {
                                const val: number =
                                    (this.sheets[0].fields["DU"].value as number) + item.netAmount;
                                this.sheets[0].fields["DU"].forceUserValue(val);
                            } else if (item.vatRate === 0) {
                                const val: number =
                                    (this.sheets[0].fields["DC"].value as number) + item.netAmount;
                                this.sheets[0].fields["DC"].forceUserValue(val);
                            } else {
                                debugger;
                            }
                        } else if (item.type === "Déductible") {
                            const val: number =
                                (this.sheets[0].fields["FB"].value as number) + item.vatAmount;
                            this.sheets[0].fields["FB"].forceUserValue(val);
                        } else if (item.type === "Intracom") {
                            (this.sheets[0].fields["DK"].displayValue as number) += item.netAmount;
                            (this.sheets[0].fields["FB"].displayValue as number) += item.vatAmount;
                        } else if (item.type === "Export") {
                            const val: number =
                                (this.sheets[0].fields["DA"].value as number) + item.netAmount;
                            this.sheets[0].fields["DA"].forceUserValue(val);
                        } else {
                            debugger;
                        }
                    }
                }
            }
            if (autoVatItems?.length) {
                // auto items
                for (const item of autoVatItems) {
                    if (item.toAdd) {
                        if (item.vatScheme === "Collectée") {
                            for (const detail of item.details) {
                                for (const vat of detail.vats) {
                                    if (vat.rate === 20) {
                                        const val: number =
                                            (this.sheets[0].fields["DK"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["DK"].forceUserValue(val);
                                    } else if (vat.rate === 5.5) {
                                        const val: number =
                                            (this.sheets[0].fields["DE"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["DE"].forceUserValue(val);
                                    } else if (vat.rate === 10) {
                                        const val: number =
                                            (this.sheets[0].fields["DQ"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["DQ"].forceUserValue(val);
                                    } else if (vat.rate === 8.5) {
                                        const val: number =
                                            (this.sheets[0].fields["DV"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["DV"].forceUserValue(val);
                                    } else if (vat.rate === 2.1) {
                                        const val: number =
                                            (this.sheets[0].fields["DU"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["DU"].forceUserValue(val);
                                    } else if (vat.rate === 0) {
                                        if (vat.vatDescription === "Export") {
                                            const val: number =
                                                (this.sheets[0].fields["DB"].value as number) -
                                                vat.netAmountSigned;
                                            this.sheets[0].fields["DB"].forceUserValue(val);
                                        } else if (!vat.vatDescription) {
                                            const val: number =
                                                (this.sheets[0].fields["DC"].value as number) -
                                                vat.netAmountSigned;
                                            this.sheets[0].fields["DC"].forceUserValue(val);
                                        }
                                    } else {
                                        debugger;
                                    }
                                }
                            }
                        } else if (item.vatScheme === "Déductible") {
                            const val: number =
                                (this.sheets[0].fields["FB"].value as number) +
                                item.totalVatToDeclare;
                            this.sheets[0].fields["FB"].forceUserValue(val);
                        } else if (item.vatScheme === "Immobilisation") {
                            const val: number =
                                (this.sheets[0].fields["FA"].value as number) +
                                item.totalVatToDeclare;
                            this.sheets[0].fields["FA"].forceUserValue(val);
                        } else if (item.vatScheme === "Acquisition intracom") {
                            (this.sheets[0].fields["PH"].displayValue as number) -=
                                item.totalGrossAmountSigned;
                            (this.sheets[0].fields["FB"].displayValue as number) +=
                                item.totalVatToDeclare;
                        } else if (item.vatScheme === "Livraison intracom") {
                            const val: number =
                                (this.sheets[0].fields["DD"].value as number) +
                                item.totalGrossAmountSigned;
                            this.sheets[0].fields["DD"].forceUserValue(val);
                        } else if (item.vatScheme === "Z de caisse") {
                            for (const detail of item.details) {
                                for (const vat of detail.vats) {
                                    if (vat.rate === 20) {
                                        const val: number =
                                            (this.sheets[0].fields["DK"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["DK"].forceUserValue(val);
                                    } else if (vat.rate === 5.5) {
                                        const val: number =
                                            (this.sheets[0].fields["DE"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["DE"].forceUserValue(val);
                                    } else if (vat.rate === 10) {
                                        const val: number =
                                            (this.sheets[0].fields["DQ"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["DQ"].forceUserValue(val);
                                    } else if (vat.rate === 8.5) {
                                        const val: number =
                                            (this.sheets[0].fields["DV"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["DV"].forceUserValue(val);
                                    } else if (vat.rate === 2.1) {
                                        const val: number =
                                            (this.sheets[0].fields["DU"].value as number) -
                                            vat.netAmountSigned;
                                        this.sheets[0].fields["DU"].forceUserValue(val);
                                    } else if (vat.rate === 0) {
                                        if (
                                            vat.vatDescription.toLowerCase().includes("intracom") &&
                                            vat.netAmountSigned < 0
                                        ) {
                                            const val: number =
                                                (this.sheets[0].fields["DD"].value as number) -
                                                vat.netAmountSigned;
                                            this.sheets[0].fields["DD"].forceUserValue(val);
                                        } else if (vat.netAmountSigned < 0) {
                                            const val: number =
                                                (this.sheets[0].fields["DC"].value as number) -
                                                vat.netAmountSigned;
                                            this.sheets[0].fields["DC"].forceUserValue(val);
                                        }
                                    } else {
                                        debugger;
                                    }
                                }
                            }
                        } else if (item.vatScheme === "Autoliquidation") {
                            const val: number =
                                (this.sheets[0].fields["DN"].value as number) -
                                item.totalGrossAmountSigned;
                            this.sheets[0].fields["DN"].forceUserValue(val);
                            const val2: number =
                                (this.sheets[0].fields["FB"].value as number) -
                                item.totalVatToDeclare;
                            this.sheets[0].fields["FB"].forceUserValue(val2);
                        } else if (item.vatScheme === "Export") {
                            const val: number =
                                (this.sheets[0].fields["DB"].value as number) +
                                item.totalGrossAmountSigned;
                            this.sheets[0].fields["DB"].forceUserValue(val);
                        } else if (item.vatScheme === "Non imposable") {
                            const val: number =
                                (this.sheets[0].fields["DC"].value as number) +
                                item.totalGrossAmountSigned;
                            this.sheets[0].fields["DC"].forceUserValue(val);
                        } else {
                            debugger;
                        }
                    }
                }
            }
        }
        // rounding
        for (const sheet of this.sheets) {
            for (const field of Object.values(sheet.fields)) {
                if (field.value) {
                    const val: number = Math.round(field.value as number);
                    field.forceUserValue(val);
                }
            }
        }
        //
        this.update();
    }

    // SPECIFIC
    // DAS2
    // fill from assist
    async fillSheetsFromDasTwoAssist(
        dasTwoDeclaring: DasTwoDeclaring,
        dasTwoBeneficiaries: DasTwoBeneficiary[],
    ) {
        if (this.cerfaNumber === "DAS2" && dasTwoDeclaring) {
            // initializing
            const declaringSheet: SheetManager = this.sheets.find(
                (sh) => sh.cerfaNumber === "DAS2T",
            );
            this.declaring = {
                name: dasTwoDeclaring.signatoryName,
                email: dasTwoDeclaring.signatoryEmail,
                phone: dasTwoDeclaring.signatoryPhone,
            };
            if (declaringSheet) {
                /// declaring info
                declaringSheet.fields["AB"].forceUserValue(dasTwoDeclaring.ape);
                declaringSheet.fields["AA_@B"].forceUserValue(dasTwoDeclaring.btq);
                declaringSheet.fields["AA_@F"].forceUserValue(dasTwoDeclaring.city);
                declaringSheet.fields["AD"].forceUserValue(dasTwoDeclaring.closingDate);
                declaringSheet.fields["AA_@I"].forceUserValue(dasTwoDeclaring.companyName);
                declaringSheet.fields["AA_@X"].forceUserValue(dasTwoDeclaring.inseeCode);
                declaringSheet.fields["AA_@A"].forceUserValue(dasTwoDeclaring.number);
                declaringSheet.fields["AA_@V"].forceUserValue(
                    dasTwoDeclaring.postOfficeDistributor,
                );
                declaringSheet.fields["AA_@E"].forceUserValue(dasTwoDeclaring.postalCode);
                declaringSheet.fields["AA_@L"].forceUserValue(dasTwoDeclaring.siret);
                declaringSheet.fields["AA_@C"].forceUserValue(dasTwoDeclaring.street);

                if (dasTwoBeneficiaries?.length) {
                    /// beneficiaries
                    const beneficiariesCount: number = dasTwoBeneficiaries.length;
                    // add pages minus one already existing
                    if (beneficiariesCount > 1) {
                        const pagesToAdd: number = beneficiariesCount - 1;
                        for (let i = 0; i < pagesToAdd; i++) {
                            await this.createAdditionalPage("DAS2TV");
                        }
                    }
                    // fill pages
                    const beneficiariesSheets: SheetManager[] = this.sheets.filter(
                        (sheet) => sheet.cerfaNumber === "DAS2TV",
                    );
                    if (beneficiariesSheets?.length) {
                        for (const [index, beneficiary] of dasTwoBeneficiaries.entries()) {
                            const beneficiarySheet: SheetManager = beneficiariesSheets[index];
                            beneficiarySheet.fields["AD"].forceUserValue(dasTwoDeclaring.siret);
                            if (beneficiary.naturalPerson) {
                                beneficiarySheet.fields["AE_@I"].forceUserValue(
                                    beneficiary.thirdPartyCompanyName,
                                );
                            } else {
                                beneficiarySheet.fields["AF_@I"].forceUserValue(
                                    beneficiary.thirdPartyCompanyName,
                                );
                            }
                            beneficiarySheet.fields["AE_@H"].forceUserValue(beneficiary.legalForm);
                            beneficiarySheet.fields["AF_@L"].forceUserValue(beneficiary.siret);
                            beneficiarySheet.fields["AG_@A"].forceUserValue(beneficiary.number);
                            beneficiarySheet.fields["AG_@B"].forceUserValue(beneficiary.btq);
                            beneficiarySheet.fields["AG_@C"].forceUserValue(beneficiary.street);
                            beneficiarySheet.fields["AG_@E"].forceUserValue(beneficiary.postalCode);
                            beneficiarySheet.fields["AG_@F"].forceUserValue(beneficiary.city);
                            beneficiarySheet.fields["AG_@X"].forceUserValue(beneficiary.inseeCode);
                            beneficiarySheet.fields["AG_@V"].forceUserValue(
                                beneficiary.postOfficeDistributor,
                            );
                            beneficiarySheet.fields["AH"].forceUserValue(beneficiary.profession);
                            beneficiarySheet.fields["CA"].forceUserValue(beneficiary.type);
                            beneficiarySheet.fields["BA"].forceUserValue(beneficiary.debit);
                        }
                    }

                    /// totals on declaring sheet
                    const totalsByType: { [key: string]: number } = dasTwoBeneficiaries.reduce(
                        (acc, { type, debit }) => {
                            if (type) {
                                acc[type] += Number(debit);
                            }
                            return acc;
                        },
                        { AR: 0, C: 0, CO: 0, DA: 0, DI: 0, H: 0, JP: 0, R: 0 },
                    );
                    let line: number = 1;
                    for (const [type, total] of Object.entries(totalsByType)) {
                        if (total > 0) {
                            if (line === 1) {
                                declaringSheet.fields["UA"].forceUserValue(type);
                                declaringSheet.fields["TA"].forceUserValue(total);
                            } else {
                                declaringSheet.fields[`UA_0${line}`].forceUserValue(type);
                                declaringSheet.fields[`TA_0${line}`].forceUserValue(total);
                            }
                            line++;
                        }
                    }
                }

                /// update
                this.update();
            } else {
                debugger;
                console.error("fillSheetsFromDasTwoAssist - no declaringSheet");
            }
        } else {
            debugger;
            console.error("fillSheetsFromDasTwoAssist - no declaring");
        }
    }

    isMandatoryForm(cerfaNumber: string) {
        // SPECIFIC
        // 1330CVAE
        // declaration automatically generated from bundle
        if (this.is1330CVAEcerfaNumber) {
            return true;
        }
        return this.mandatoryForms.some(
            (form) =>
                form.cerfa === cerfaNumber ||
                (form.forms && form.forms.some((subForm) => subForm.cerfa === cerfaNumber)),
        );
    }

    fillAutoDeposit() {
        if (this.cerfaNumber === "3514") {
            const val: number = (this.autoDeposit as AutoDeposit3514)?.amount || null;
            this.sheets[0].fields["HA"].forceUserValue(val);
            const val2: number =
                (this.autoDeposit as AutoDeposit3514)?.carriedForwardCreditAmount || null;
            this.sheets[0].fields["HB"].forceUserValue(val2);
        } else if (this.cerfaNumber === "2571") {
            const val: number =
                (this.autoDeposit as AutoDeposit2571)?.amounts?.find(
                    (amount) => amount.field === "A011",
                )?.amount || null;
            this.sheets[0].fields["ZT"].forceUserValue(val);
            const val2: number =
                (this.autoDeposit as AutoDeposit2571)?.amounts?.find(
                    (amount) => amount.field === "A021",
                )?.amount || null;
            this.sheets[0].fields["ZB"].forceUserValue(val2);
            const val3: number =
                (this.autoDeposit as AutoDeposit2571)?.amounts?.find(
                    (amount) => amount.field === "A05",
                )?.amount || null;
            this.sheets[0].fields["ZF"].forceUserValue(val3);
        }
        this.update();
    }
}

type AutoDeposit = AutoDeposit3514 | AutoDeposit2571;

type AutoDeposit2571 = {
    amounts: {
        field: "A021" | "A011" | "A05";
        fieldText: string;
        amount: number;
    }[];
};

type AutoDeposit3514 = {
    amount: number;
    field: string;
    fieldText: string;
    carriedForwardCreditAmount: number;
    carriedForwardCreditField: string;
    carriedForwardCreditFieldText: string;
};
